import { Injectable } from '@angular/core';
import { HomesFacade } from '@library/store/homes/homes.facade';
import { Home } from '@library/store/homes/homes.interface';
import { EnumMeasureTypes } from '@library/store/measures/measures.interface';
import { ModulesFacade } from '@library/store/modules/modules.facade';
import { SourceType } from '@library/store/modules/modules.interface';
import { RoomsFacade } from '@library/store/rooms/rooms.facade';
import { Room } from '@library/store/rooms/rooms.interface';
import { isDefined } from '@library/utils/helpers/store/is-defined';
import { LangPipe } from '@library/utils/pipes/lang.pipe';
import { MeasureCard, YScaleType } from '@pages/control-page/control-page-main/control-page-compare/cp-compare-sidebar/cp-compare-sidebar.service';
import { ControlPageCardService } from '@pages/control-page/control-page-main/control-page-dashboard/control-page-sidebar/control-page-card.service';
import { CO2_MODULES, FAVORITE_TEMPERATURE_MODULES, HUMIDITY_MODULES, MODULE_ICONS, NOISE_MODULES, PRESSURE_MODULES, RAIN_MODULES, SUPPORTED_ECOMETER_MODULES, SUPPORTED_ELEC_MODULES, TEMPERATURE_MODULES, THERMOSTAT_MODULES, VALVE_MODULES, WEATHER_TEMPERATURE_MODULES, WIND_MODULES } from '@pages/control-page/control-page.const';
import { Enums } from 'constants/Enums.constant';
import { firstValueFrom, map, shareReplay, switchMap } from 'rxjs';

@Injectable()
export class CpCompareSidebarCardsService {

  measureCards$ = this.homesFacade.currentHomeId$.pipe(isDefined()).pipe(
    switchMap(() => this.buildMeasureCards()),
    shareReplay(1),
  );

  roomMeasureTypeCards = (roomId: string, measureType: EnumMeasureTypes) => this.measureCards$.pipe(
    map(measureCards => measureCards.filter(measureCard =>
      (measureCard.room?.id === roomId || measureCard.module?.room_id === roomId)
        && measureCard.measureType === measureType
    )),
    isDefined(),
  );

  constructor(
    private homesFacade: HomesFacade,
    private langPipe: LangPipe,
    private roomsFacade: RoomsFacade,
    private controlPageCardService: ControlPageCardService,
    private modulesFacade: ModulesFacade,
  ) {}

  private async buildMeasureCards() {
    const measureCards: MeasureCard[] = [];
    const rooms = await firstValueFrom(this.roomsFacade.currentHomeRooms$);

    for (const room of rooms) {
      const roomMeasureCards = await this.buildRoomMeasureCards(room);
      measureCards.push(...roomMeasureCards);
    }

    const home = await firstValueFrom(this.homesFacade.currentHome$);
    const homeMeasureCards = await this.buildHomeMeasureCards(home);
    measureCards.push(...homeMeasureCards);

    return measureCards;
  }

  private async buildHomeMeasureCards(home: Home) {
    const measureCards: MeasureCard[] = [];

    const modules = await firstValueFrom(this.modulesFacade.currentHomeModules$);

    const currencySymbol = await firstValueFrom(this.homesFacade.currencySymbol$);

    const hasElec = modules.some(m => SUPPORTED_ELEC_MODULES.includes(m.type));
    const hasEcometer = modules.some(m => SUPPORTED_ECOMETER_MODULES.includes(m.type));

    if (hasElec) {
      measureCards.push(
        { ...ElectricityMeasureCard, home: home, label: this.langPipe.transform('common-app.__WEBAPP_NEW_COMPARE_ENERGY') },
        { ...ElectricityPriceMeasureCard, home: home, label: this.langPipe.transform('common-app.__WEBAPP_NEW_COMPARE_PRICE', [currencySymbol]) },
      );
    }

    const coldWaterModule = modules.find(m => m.type === Enums.ModuleType.NLE && m.source_type === SourceType.COLD_WATER);

    if (hasEcometer) {
      measureCards.push(
        {...ColdWaterMeasureCard,       module: coldWaterModule,  label: this.langPipe.transform('common-app.__WEBAPP_NEW_COMPARE_VOLUME_L') },
        {...ColdWaterPriceMeasureCard,  module: coldWaterModule,  label: this.langPipe.transform('common-app.__WEBAPP_NEW_COMPARE_PRICE', [currencySymbol]) },
      );
    }

    const hotWaterModule = modules.find(m => m.type === Enums.ModuleType.NLE && m.source_type === SourceType.HOT_WATER);

    if (hasEcometer) {
      measureCards.push(
        {...HotWaterMeasureCard,        module: hotWaterModule,   label: this.langPipe.transform('common-app.__WEBAPP_NEW_COMPARE_VOLUME_L') },
        {...HotWaterPriceMeasureCard,   module: hotWaterModule,   label: this.langPipe.transform('common-app.__WEBAPP_NEW_COMPARE_PRICE', [currencySymbol]) },
      );
    }

    const gasModule = modules.find(m => m.type === Enums.ModuleType.NLE && m.source_type === SourceType.GAS);

    if (hasEcometer) {
      measureCards.push(
        {...GasMeasureCard, module: gasModule, label: this.langPipe.transform('common-app.__WEBAPP_NEW_COMPARE_VOLUME_DM3') },
        {...GasPriceMeasureCard, module: gasModule, label: this.langPipe.transform('common-app.__WEBAPP_NEW_COMPARE_PRICE', [currencySymbol])},
      );
    }

    return measureCards;
  }

  private async buildRoomMeasureCards(room: Room) {
    const measureCards: MeasureCard[] = [];

    for (const measureType of SupportedMeasureTypes) {
      let cards: MeasureCard[] = [];
      switch (measureType) {
        case EnumMeasureTypes.SUM_BOILER_ON:
          cards = await this.buildBoilerOnMeasureCards(room);
          break;
        case EnumMeasureTypes.CO2:
          cards = await this.buildCo2MeasureCards(room);
          break;
        case EnumMeasureTypes.GUST_STRENGTH:
          cards = await this.buildGustStrengthMeasureCards(room);
          break;
        case EnumMeasureTypes.HEATING_POWER_REQUEST:
          cards = await this.buildHeatingPowerRequestMeasureCards(room);
          break;
        case EnumMeasureTypes.HUMIDITY:
          cards = await this.buildHumidityMeasureCards(room);
          break;
        case EnumMeasureTypes.NOISE:
          cards = await this.buildNoiseMeasureCards(room);
          break;
        case EnumMeasureTypes.PRESSURE:
          cards = await this.buildPressureMeasureCards(room);
          break;
        case EnumMeasureTypes.SUM_RAIN:
          cards = await this.buildRainMeasureCards(room);
          break;
        case EnumMeasureTypes.TEMPERATURE:
          cards = await this.buildTemperatureMeasureCards(room);
          break;
        case EnumMeasureTypes.WIND_STRENGTH:
          cards = await this.buildWindStrengthMeasureCards(room);
          break;
      }

      measureCards.push(...cards);
    }

    return measureCards;
  }

  private async buildBoilerOnMeasureCards(room: Room) {
    const modules = await firstValueFrom(this.controlPageCardService.roomModules(room.id));
    const thermostatModules = modules.filter(module => THERMOSTAT_MODULES.includes(module.type));

    const measureCards: MeasureCard[] = [];

    thermostatModules.forEach(module => {
      let label = `${room.name} - ${this.langPipe.transform('energie-app.__COURBES_BOILER_ON')}`;
      if (thermostatModules.length > 1) {
        label += ` - ${module.name}`;
      }

      measureCards.push({
        label: label,
        sidebarLabel: module.name ?? 'energie-app.__COURBES_BOILER_ON',
        type: 'custom-bar',
        measureType: EnumMeasureTypes.SUM_BOILER_ON,
        yScaleType: YScaleType.PERCENTAGE,
        module: module,
        icon: MODULE_ICONS[module.type] ?? 'heater',
      });
    });
    return measureCards;
  }

  private async buildCo2MeasureCards(room: Room) {
    const measureCards: MeasureCard[] = [];

    let modules = await firstValueFrom(this.controlPageCardService.roomModules(room.id));
    modules = modules.filter(module => module.mode !== 'favorite');

    const co2Modules = modules.filter(module => CO2_MODULES.includes(module.type));

    co2Modules.forEach(module => {
      let label = `${room.name} - ${this.langPipe.transform('common-app.__CO2')}`;
      if (co2Modules.length > 1) {
        label += ` - ${module.name}`;
      }

      measureCards.push({
        label: label,
        sidebarLabel: module.name ?? 'common-app.__CO2',
        type: 'line',
        measureType: EnumMeasureTypes.CO2,
        yScaleType: YScaleType.PPM,
        module: module,
        icon: MODULE_ICONS[module.type] ?? 'co-2',
      });
    });

    return measureCards;
  }

  private async buildGustStrengthMeasureCards(room: Room) {
    const modules = await firstValueFrom(this.controlPageCardService.roomModules(room.id));
    const gustStrengthModules = modules.filter(module => WIND_MODULES.includes(module.type));

    const measureCards: MeasureCard[] = [];

    gustStrengthModules.forEach(module => {
      let label = `${room.name} - ${this.langPipe.transform('station-app.__GUST_SPEED')}`;
      if (gustStrengthModules.length > 1) {
        label += ` - ${module.name}`;
      }

      measureCards.push({
        label: label,
        sidebarLabel: module.name ?? 'station-app.__GUST_SPEED',
        type: 'line',
        measureType: EnumMeasureTypes.GUST_STRENGTH,
        yScaleType: YScaleType.SPEED,
        module: module,
        icon: MODULE_ICONS[module.type] ?? 'wind',
      });
    });
    return measureCards;
  }

  private async buildHeatingPowerRequestMeasureCards(room: Room) {
    const modules = await firstValueFrom(this.controlPageCardService.roomModules(room.id));
    const thermostatModules = modules.filter(module => THERMOSTAT_MODULES.includes(module.type));
    const vavleModules = modules.filter(module => VALVE_MODULES.includes(module.type));

    const measureCards: MeasureCard[] = [];

    if (thermostatModules.length === 0 && vavleModules.length > 0) {

      measureCards.push({
        label: `${room.name} - ${this.langPipe.transform('energie-app.__COURBES_HEATING_POWER')}`,
        sidebarLabel: 'energie-app.__COURBES_HEATING_POWER',
        type: 'custom-bar',
        measureType: EnumMeasureTypes.HEATING_POWER_REQUEST,
        yScaleType: YScaleType.PERCENTAGE,
        room: room,
        icon: 'heater',
      });

    }
    return measureCards;
  }

  private async buildHumidityMeasureCards(room: Room) {
    let modules = await firstValueFrom(this.controlPageCardService.roomModules(room.id));
    modules = modules.filter(module => {
      if (module.mode === 'favorite') {
        return FAVORITE_TEMPERATURE_MODULES.includes(module.type);
      }
      else {
        return true;
      }
    });
    const humidityModules = modules.filter(module => HUMIDITY_MODULES.includes(module.type));

    const measureCards: MeasureCard[] = [];

    humidityModules.forEach(module => {
      let label = `${room.name} - ${this.langPipe.transform('common-app.__HUMIDITY')}`;
      if (humidityModules.length > 1) {
        label += ` - ${module.name}`;
      }

      measureCards.push({
        label: label,
        sidebarLabel: module.name ?? 'common-app.__HUMIDITY',
        type: 'line',
        measureType: EnumMeasureTypes.HUMIDITY,
        yScaleType: YScaleType.PERCENTAGE,
        module: module,
        icon: MODULE_ICONS[module.type] ?? 'humidity',
      });
    });
    return measureCards;
  }

  private async buildNoiseMeasureCards(room: Room) {
    let modules = await firstValueFrom(this.controlPageCardService.roomModules(room.id));
    modules = modules.filter(module => module.mode !== 'favorite');

    const noiseModules = modules.filter(module => NOISE_MODULES.includes(module.type));

    const measureCards: MeasureCard[] = [];

    noiseModules.forEach(module => {
      let label = `${room.name} - ${this.langPipe.transform('common-app.__NOISE')}`;
      if (noiseModules.length > 1) {
        label += ` - ${module.name}`;
      }

      measureCards.push({
        label: label,
        sidebarLabel: module.name ?? 'common-app.__NOISE',
        type: 'line',
        measureType: EnumMeasureTypes.NOISE,
        yScaleType: YScaleType.NOISE,
        module: module,
        icon: MODULE_ICONS[module.type] ?? 'sound',
      });
    });
    return measureCards;
  }

  private async buildPressureMeasureCards(room: Room) {
    const modules = await firstValueFrom(this.controlPageCardService.roomModules(room.id));
    const pressureModules = modules.filter(module => PRESSURE_MODULES.includes(module.type));

    const measureCards: MeasureCard[] = [];

    pressureModules.forEach(module => {
      let label = `${room.name} - ${this.langPipe.transform('common-app.__PRESSURE')}`;
      if (pressureModules.length > 1) {
        label += ` - ${module.name}`;
      }
      measureCards.push({
        label: label,
        sidebarLabel: module.name ?? 'common-app.__PRESSURE',
        type: 'line',
        measureType: EnumMeasureTypes.PRESSURE,
        yScaleType: YScaleType.PRESSURE,
        module: module,
        icon: MODULE_ICONS[module.type] ?? 'pressure',
      });
    });
    return measureCards;
  }

  private async buildRainMeasureCards(room: Room) {
    const modules = await firstValueFrom(this.controlPageCardService.roomModules(room.id));
    const rainModules = modules.filter(module => RAIN_MODULES.includes(module.type));

    const measureCards: MeasureCard[] = [];

    rainModules.forEach(module => {
      let label = `${room.name} - ${this.langPipe.transform('common-app.__RAIN')}`;
      if (rainModules.length > 1) {
        label += ` - ${module.name}`;
      }
      measureCards.push({
        label: label,
        sidebarLabel: module.name ?? 'common-app.__RAIN',
        type: 'custom-bar',
        measureType: EnumMeasureTypes.SUM_RAIN,
        yScaleType: YScaleType.RAIN,
        module: module,
        icon: MODULE_ICONS[module.type] ?? 'water',
      });
    });
    return measureCards;
  }

  private async buildTemperatureMeasureCards(room: Room) {
    let modules = await firstValueFrom(this.controlPageCardService.roomModules(room.id));
    modules = modules.filter(module => {
      if (module.mode === 'favorite') {
        return FAVORITE_TEMPERATURE_MODULES.includes(module.type);
      }
      else {
        return true;
      }
    });
    const temperatureModules = modules.filter(module => TEMPERATURE_MODULES.includes(module.type));

    const valves = temperatureModules.filter(module => VALVE_MODULES.includes(module.type));
    const thermostats = temperatureModules.filter(module => THERMOSTAT_MODULES.includes(module.type));
    const weatherModules = temperatureModules.filter(module => WEATHER_TEMPERATURE_MODULES.includes(module.type));

    const measureCards: MeasureCard[] = [];

    if (thermostats.length > 0) {
      let label = `${room.name} - ${this.langPipe.transform('common-app.__TEMPERATURE')}`;
      if (weatherModules.length > 0) {
        label += ` - ${this.langPipe.transform('common-app.__THERMOSTAT_TITLE')}`;
      }
      measureCards.push({
        type: 'line',
        measureType: EnumMeasureTypes.TEMPERATURE,
        yScaleType: YScaleType.TEMPERATURE,
        room: room as Room,
        icon: 'thermostat',
        label: label,
        sidebarLabel: 'common-app.__THERMOSTAT_TITLE',
      });
    }
    else if (valves.length > 0) {
      let sidebarLabel = 'energie-app.__VALVE';
      if (valves.length > 1) {
        sidebarLabel = 'energie-app.__VALVES';
      }

      let label = `${room.name} - ${this.langPipe.transform('common-app.__TEMPERATURE')}`;
      if (weatherModules.length > 0) {
        label += ` - ${this.langPipe.transform(sidebarLabel)}`;
      }

      measureCards.push({
        icon: 'temperature',
        type: 'line',
        measureType: EnumMeasureTypes.TEMPERATURE,
        yScaleType: YScaleType.TEMPERATURE,
        room: room as Room,
        label: label,
        sidebarLabel: sidebarLabel,
      });
    }

    weatherModules.forEach(module => {
      let label = `${room.name} - ${this.langPipe.transform('common-app.__TEMPERATURE')}`;
      if (measureCards.length + weatherModules.length > 1) {
        label += ` - ${module.name}`;
      }
      measureCards.push({
        type: 'line',
        measureType: EnumMeasureTypes.TEMPERATURE,
        yScaleType: YScaleType.TEMPERATURE,
        module: module,
        icon: MODULE_ICONS[module.type] ?? 'temperature',
        label: label,
        sidebarLabel: module.name,
      });
    });
    return measureCards;
  }

  private async buildWindStrengthMeasureCards(room: Room) {
    const modules = await firstValueFrom(this.controlPageCardService.roomModules(room.id));
    const windStrengthModules = modules.filter(module => WIND_MODULES.includes(module.type));

    const measureCards: MeasureCard[] = [];

    windStrengthModules.forEach(module => {
      let label = `${room.name} - ${this.langPipe.transform('legrand-app.__WIND_SPEED')}`;
      if (windStrengthModules.length > 1) {
        label += ` - ${module.name}`;
      }
      return measureCards.push({
        label: label,
        sidebarLabel: module.name ?? 'legrand-app.__WIND_SPEED',
        type: 'line',
        measureType: EnumMeasureTypes.WIND_STRENGTH,
        yScaleType: YScaleType.SPEED,
        module: module,
        icon: MODULE_ICONS[module.type] ?? 'wind',
      });
    });
    return measureCards;
  }
}

const SupportedMeasureTypes = [
  EnumMeasureTypes.SUM_BOILER_ON,
  EnumMeasureTypes.CO2,
  EnumMeasureTypes.GUST_STRENGTH,
  EnumMeasureTypes.HEATING_POWER_REQUEST,
  EnumMeasureTypes.HUMIDITY,
  EnumMeasureTypes.NOISE,
  EnumMeasureTypes.PRESSURE,
  EnumMeasureTypes.SUM_RAIN,
  EnumMeasureTypes.TEMPERATURE,
  EnumMeasureTypes.WIND_STRENGTH,
];

const ElectricityMeasureCard = {
  icon: 'flash',
  type: 'custom-bar',
  measureType: EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_BASE,
  yScaleType: YScaleType.ELECTRICITY,
} as MeasureCard;

const ElectricityPriceMeasureCard = {
  icon: 'flash',
  type: 'custom-bar',
  measureType: EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_PRICE_BASE,
  yScaleType: YScaleType.PRICE,
} as MeasureCard;

const HotWaterMeasureCard = {
  icon: 'humidity',
  type: 'custom-bar',
  measureType: EnumMeasureTypes.SUM_FLUID_CONSUMPTION_HOT_WATER,
  yScaleType: YScaleType.WATER,
} as MeasureCard;

const ColdWaterMeasureCard = {
  icon: 'humidity',
  type: 'custom-bar',
  measureType: EnumMeasureTypes.SUM_FLUID_CONSUMPTION_COLD_WATER,
  yScaleType: YScaleType.WATER,
} as MeasureCard;

const HotWaterPriceMeasureCard = {
  icon: 'humidity',
  type: 'custom-bar',
  measureType: EnumMeasureTypes.SUM_FLUID_PRICE_HOT_WATER,
  yScaleType: YScaleType.PRICE,
} as MeasureCard;

const ColdWaterPriceMeasureCard = {
  icon: 'humidity',
  type: 'custom-bar',
  measureType: EnumMeasureTypes.SUM_FLUID_PRICE_COLD_WATER,
  yScaleType: YScaleType.PRICE,
} as MeasureCard;

const GasMeasureCard = {
  icon: 'gas',
  type: 'custom-bar',
  measureType: EnumMeasureTypes.SUM_FLUID_CONSUMPTION_GAS,
  yScaleType: YScaleType.GAS,
} as MeasureCard;

const GasPriceMeasureCard = {
  icon: 'gas',
  type: 'custom-bar',
  measureType: EnumMeasureTypes.SUM_FLUID_PRICE_GAS,
  yScaleType: YScaleType.PRICE,
} as MeasureCard;
